// src/components/auth/components/ResetPasswordForm.tsx
import React, { useMemo, useState } from "react";
import PasswordField from "./PasswordField";
import {
  calcPasswordStrength,
  strengthColor,
  strengthLabel,
} from "@/components/auth/utils/passwordStrength";

type Props = {
  onSubmit: (payload: { password: string; confirmPassword: string }) =>
    | void
    | Promise<void>;
  loading?: boolean;
  error?: string | null;
  success?: boolean;
};

export default function ResetPasswordForm({
  onSubmit,
  loading = false,
  error,
  success,
}: Props) {
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");

  const strength = useMemo(() => calcPasswordStrength(newPwd), [newPwd]);
  const pwdMatch = newPwd === confirmPwd && newPwd.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPwd || !confirmPwd) {
      alert("Please fill all fields.");
      return;
    }
    if (!pwdMatch) {
      alert("New passwords do not match.");
      return;
    }
    if (strength < 3) {
      alert("Please choose a stronger password.");
      return;
    }

    await onSubmit({ password: newPwd, confirmPassword: confirmPwd });

    // Only clear if successful
    if (success) {
      setNewPwd("");
      setConfirmPwd("");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-4">
      {/* Messages */}
      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg bg-green-50 px-3 py-2 text-xs text-green-700">
          Your password has been reset successfully. You can now log in with
          your new password.
        </div>
      )}

      {/* New password + strength */}
      <div>
        <PasswordField
          label="New password"
          value={newPwd}
          onChange={setNewPwd}
          placeholder="At least 8 characters"
          autoComplete="new-password"
          // disabled={loading}
        />

        {/* strength meter */}
        <div className="mt-2">
          <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{
                width: `${(strength / 5) * 100}%`,
                backgroundColor: strengthColor(strength),
              }}
            />
          </div>
          <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500">
            <span>
              Strength:{" "}
              <span style={{ color: strengthColor(strength) }}>
                {strengthLabel(strength)}
              </span>
            </span>
            <span>Use Aa · 0-9 · !@#</span>
          </div>
        </div>
      </div>

      {/* Confirm new password */}
      <div>
        <PasswordField
          label="Confirm new password"
          value={confirmPwd}
          onChange={setConfirmPwd}
          placeholder="Re-enter new password"
          autoComplete="new-password"
          // disabled={loading}
        />
        {!pwdMatch && confirmPwd.length > 0 && (
          <p className="mt-1 text-[11px] text-red-600">
            Passwords don’t match
          </p>
        )}
      </div>

      <button
        type="submit"
        className="w-full rounded-xl bg-sky-500 py-2 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:opacity-60"
        disabled={loading || !pwdMatch || strength < 3}
      >
        {loading ? "Updating..." : "Set New Password"}
      </button>
    </form>
  );
}
